// Miles are transported as integers in base units with 9 decimals.
// All parsing is string-based to avoid binary floating point error.

export const MILES_DECIMALS = 9;

/** One whole mile expressed in base units. */
export const MILE = 10 ** MILES_DECIMALS;

// Plain digits or correctly grouped thousands ("1,250"), up to 9 decimals.
const MILES_INPUT = /^(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d{0,9})?$/;

export function isValidMilesInput(input: string): boolean {
  return toBaseUnits(input) !== null;
}

/**
 * Parse a user-entered miles amount ("12.5", "1,250") into base units.
 * Returns null for anything that is not a plain non-negative decimal with
 * at most 9 fractional digits, or that would exceed Number.MAX_SAFE_INTEGER.
 */
export function toBaseUnits(input: string): number | null {
  if (typeof input !== "string") {
    return null;
  }

  const trimmed = input.trim();

  if (!MILES_INPUT.test(trimmed)) {
    return null;
  }

  const cleaned = trimmed.replace(/,/g, "");

  const [whole, fraction = ""] = cleaned.split(".");
  const digits = whole + fraction.padEnd(MILES_DECIMALS, "0");
  const value = BigInt(digits);

  if (value > BigInt(Number.MAX_SAFE_INTEGER)) {
    return null;
  }

  return Number(value);
}

/** Render base units as a decimal miles string with trailing zeros trimmed. */
export function fromBaseUnits(baseUnits: number | bigint | string): string {
  const value = BigInt(baseUnits);
  const negative = value < 0n;
  const abs = negative ? -value : value;
  const digits = abs.toString().padStart(MILES_DECIMALS + 1, "0");
  const whole = digits.slice(0, -MILES_DECIMALS);
  const fraction = digits.slice(-MILES_DECIMALS).replace(/0+$/, "");

  return (negative ? "-" : "") + whole + (fraction ? "." + fraction : "");
}

/** Multiply a base-unit amount by a quantity; returns a decimal string of base units. */
export function mulBaseUnits(baseUnits: number | bigint | string, quantity: number): string {
  return (BigInt(baseUnits) * BigInt(quantity)).toString();
}
